import { CommonModule } from '@angular/common';
import {
    Component,
    OnInit,
    ViewChild,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FieldType, FieldTypeConfig, FormlyModule } from '@ngx-formly/core';
import {
    MatAutocompleteModule,
    MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { MatInput, MatInputModule } from '@angular/material/input';
import { Observable, debounceTime, startWith, switchMap } from 'rxjs';
import { ErrorStateMatcher } from '@angular/material/core';
import { SalesOfferRuleType } from '@zopup/zop-types';

@Component({
    selector: 'zop-autocomplete',
    imports: [
        CommonModule,
        FormlyModule,
        ReactiveFormsModule,
        FormsModule,
        MatAutocompleteModule,
        MatInputModule,
    ],
    template: `
        <mat-form-field appearance="outline" class="full-width">
            <mat-label>
                {{ to.label }}
                <span *ngIf="to.required" class="mat-form-field-required-marker"> *</span>
            </mat-label>
            <input
                matInput
                [matAutocomplete]="auto"
                [formControl]="formControl"
                [formlyAttributes]="field"
                [errorStateMatcher]="errorStateMatcher"
            />
            <mat-autocomplete
                #auto="matAutocomplete"
                [displayWith]="displayFn.bind(this)"
                (optionSelected)="onSelectionChange($event)"
            >
                <mat-option *ngFor="let item of items | async" [value]="item">
                    {{ item.label }}
                </mat-option>
            </mat-autocomplete>

            <mat-error *ngIf="showError">
                <formly-validation-message [field]="field"></formly-validation-message>
            </mat-error>
        </mat-form-field>
    `,
    styles: [
        `
            .full-width {
                width: 100%;
            }
            mat-form-field {
                display: flex;
                flex-direction: column;
            }
        `,
    ]
})
export class ZopAutoCompleteComponent
    extends FieldType<FieldTypeConfig>
    implements OnInit
{
    @ViewChild(MatInput) formFieldControl!: MatInput;
    @ViewChild(MatAutocompleteTrigger) autocomplete!: MatAutocompleteTrigger;
    items?: Observable<
        {
            label: string;
            value: string;
            id: string;
            type: SalesOfferRuleType;
        }[]
    >;
    errorStateMatcher!: ErrorStateMatcher;
    customerSelected: any;

    ngOnInit() {
        this.items = this.formControl.valueChanges.pipe(
            startWith(this.formControl.value || ''),
            debounceTime(200),
            switchMap((term) => {
                // This control carries two different things through the same valueChanges stream:
                // the user's typed search text (always a string) while typing, and the selected
                // AutocompleteData object once an option is picked (see onSelectionChange above —
                // it sets this same control's value to the full object, not just its display
                // string). Only a genuine typed string should re-run the search; re-running it with
                // the just-selected object as the "term" produced a nonsense
                // `searchText=[object Object]` request on every selection (and on every edit-mode
                // load, since `startWith(this.formControl.value)` above hits the same path when the
                // field opens pre-filled with an object) — getSingle (fetchById, wired by the
                // consumer's own valueChanges subscription) is the only call selecting an option
                // should trigger.
                if (typeof term !== 'string') {
                    return [] as unknown as Observable<AutocompleteData[]>;
                }
                if (this.props?.['filter'] && typeof this.props['filter'] === 'function') {
                    return this.props['filter'](term) as Observable<
                        AutocompleteData[]
                    >;
                }
                return [] as unknown as Observable<AutocompleteData[]>;
            })
        );

        // this.items = this.props.options as Observable<AutocompleteData[]>;
    }


    /** Formly can set the initial/prefilled model value as a plain string (e.g. a currency code
     * like "USD") rather than the full AutocompleteData object a user selection produces —
     * Angular Material's displayWith calls this on writeValue() with whatever raw value the form
     * control holds, so a string must render as itself, not fall through to ''. */
    displayFn(item: any): string {
        if (item && typeof item === 'object' && item.label) return item.label;
        if (typeof item === 'string') return item;
        return '';
    }

    onSelectionChange(event: any) {
        const selectedValue = event.option.value;
        // emitEvent: false — Angular Material's MatAutocompleteTrigger already propagates the
        // selected option into this bound [formControl] automatically on selection (its internal
        // ControlValueAccessor onChange call), which already fires one valueChanges emission on
        // its own. Without this flag, the explicit setValue() below fired a second, redundant
        // valueChanges emission for the same single click — harmless for a plain display value,
        // but any consumer subscribed to valueChanges to react to a selection (e.g. Purchase
        // Order's vendor picker, which fetches the full vendor record on selection) ran its
        // side effect twice per click. This setValue() call still keeps the control's value
        // authoritative/explicit; it just no longer duplicates Material's own emission.
        this.formControl.setValue(selectedValue, { emitEvent: false });
        this.customerSelected = selectedValue;
    }
}

export interface AutocompleteData {
    label: string;
    value: any;
    id: string;
    type: SalesOfferRuleType;
    extra?: any;
}
